import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useHoras, useNomina, useReporteAcumuladoMO, useSubcontratos } from '@/hooks/useManoObra';
import { useM2 } from '@/hooks/useReportes';
import { type ManoObraFila, calcularManoObra } from '@/lib/calcularManoObra';
import type { FiltroVenta } from '@/lib/filtroVenta';
import { cn } from '@/lib/utils';
import { type ReporteSemanal, type SemanaOperativa, formatearCRC } from '@adelante/shared';
import { ArrowDown, ArrowUp, ArrowsDownUp, CircleNotch } from '@phosphor-icons/react';
import { type ReactNode, useMemo, useState } from 'react';
import { ResumenMesMOVista } from './ResumenMesMOVista';

// Columnas ordenables de la distribución por obra.
type MoCol = 'codigo' | 'hrs' | 'pctHoras' | 'nominaAsig' | 'sc' | 'moTotal' | 'm2' | 'costoM2';
type MoOrden = { col: MoCol; dir: 'asc' | 'desc' };
type Periodo = 'semana' | 'acumulado';

/**
 * Tab "Mano de Obra" del reporte. Consolida la captura (nómina, horas,
 * subcontratos) con los m² avanzados de la semana para mostrar:
 *   - Horas Hombre (HH directas, equiv. subcontrato, HH/m², costo prom HH)
 *   - Costos M.O. (directa, subcontrato, total, presupuestada, sobrecosto)
 *   - Distribución por obra.
 * Todo en frontend. Respeta el filtro de venta (formalizadas / no).
 */
export function ManoObraReporteVista({
  semana,
  reporte,
  semanas,
  filtroVenta,
}: {
  semana: number | null;
  reporte: ReporteSemanal | null;
  semanas: SemanaOperativa[];
  filtroVenta: FiltroVenta;
}) {
  const { data: nominaD } = useNomina();
  const { data: horasD } = useHoras();
  const { data: subcD } = useSubcontratos();
  const { data: acumD, isLoading: acumLoading } = useReporteAcumuladoMO();
  const { data: m2D } = useM2(semana);

  const calc = useMemo(() => {
    if (!semana || !reporte) return null;
    return calcularManoObra(
      Number(semana),
      reporte,
      nominaD?.data ?? [],
      horasD?.data ?? [],
      subcD?.data ?? [],
    );
  }, [semana, reporte, nominaD, horasD, subcD]);

  // Distribución acumulada por obra: nómina prorrateada + subcontratos de todo el
  // histórico, combinada con el m² acumulado a la fecha (mismo dato de `/reportes/m2`).
  const filasAcumulado = useMemo<ManoObraFila[]>(() => {
    const obrasAcum = acumD?.data?.obras ?? [];
    const m2PorObra = new Map((m2D?.data?.obras ?? []).map((o) => [o.codigo, o.m2_acumulado]));
    const horasTotal = obrasAcum.reduce((a, o) => a + o.horas_acum, 0);
    return obrasAcum.map((o) => {
      const m2 = m2PorObra.get(o.obra_codigo) ?? 0;
      return {
        codigo: o.obra_codigo,
        hrs: o.horas_acum,
        pctHoras: horasTotal > 0 ? (o.horas_acum / horasTotal) * 100 : 0,
        nominaAsig: o.nomina_asignada_acum,
        sc: o.subcontratos_acum,
        moTotal: o.mo_total_acum,
        m2,
        costoM2: m2 > 0 ? o.mo_total_acum / m2 : 0,
      };
    });
  }, [acumD, m2D]);

  const [busqueda, setBusqueda] = useState('');
  const [orden, setOrden] = useState<MoOrden>({ col: 'moTotal', dir: 'desc' });
  const [periodo, setPeriodo] = useState<Periodo>('semana');

  if (!calc) {
    return <p className="py-8 text-center text-sm text-muted-foreground">Seleccioná una semana.</p>;
  }

  // Distribución filtrada por búsqueda + ordenada por la columna elegida.
  const filtro = busqueda.trim().toLowerCase();
  const filas = periodo === 'acumulado' ? filasAcumulado : calc.obras;
  const vista = filas
    .filter((o) => !filtro || o.codigo.toLowerCase().includes(filtro))
    .sort((a, b) => {
      const av = a[orden.col];
      const bv = b[orden.col];
      const cmp =
        typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av).localeCompare(String(bv), 'es', { numeric: true });
      return orden.dir === 'asc' ? cmp : -cmp;
    });

  return (
    <div className="space-y-4">
      {!calc.tieneNomina && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          ⓘ No hay <strong>nómina registrada</strong> para esta semana — registrala en Admin → M.O.
          · Nómina para ver los indicadores.
        </div>
      )}

      {/* Producción relevante */}
      <Seccion titulo="Producción">
        <Kpi label="m² Construidos Semana" value={fmt(calc.m2Sem)} accent="lime" />
        <Kpi label="Costo teórico/m²" value={formatearCRC(calc.teorico)} small />
        <Kpi
          label="Eficiencia"
          value={calc.eficiencia > 0 ? `${calc.eficiencia.toFixed(0)}%` : '—'}
          accent={calc.eficiencia >= 100 ? 'lime' : 'red'}
        />
      </Seccion>

      {/* Horas Hombre */}
      <Seccion titulo="Horas Hombre">
        <Kpi label="HH Directas Semana" value={fmt(calc.hhDirectas)} />
        <Kpi
          label="HH Equiv. Subcontrato"
          value={calc.hhEquivSubc > 0 ? fmt(calc.hhEquivSubc) : '—'}
        />
        <Kpi label="Costo Prom HH C/Cargas" value={formatearCRC(calc.costoPromHH)} small />
        <Kpi label="HH/m² Sin Subcontrato" value={calc.hhM2Sin > 0 ? fmt2(calc.hhM2Sin) : '—'} />
        <Kpi label="HH/m² Con Subcontrato" value={calc.hhM2Con > 0 ? fmt2(calc.hhM2Con) : '—'} />
        <Kpi label="Costo MO por m²" value={formatearCRC(calc.costoMOm2)} small />
      </Seccion>

      {/* Costos Mano de Obra */}
      <Seccion titulo="Costos Mano de Obra">
        <Kpi label="MO Directa Semana" value={formatearCRC(calc.moDirecta)} small />
        <Kpi
          label="MO Subcontrato"
          value={calc.moSubcontrato > 0 ? formatearCRC(calc.moSubcontrato) : '—'}
          small
        />
        <Kpi
          label="MO Total Gastada"
          value={formatearCRC(calc.moTotalGastada)}
          small
          accent="lime"
        />
        <Kpi label="MO Presupuestada" value={formatearCRC(calc.moPresupuestada)} small />
        <Kpi
          label="Sobrecosto MO"
          value={formatearCRC(calc.sobrecosto)}
          small
          accent={calc.sobrecosto > 0 ? 'red' : 'lime'}
        />
      </Seccion>

      <p className="text-[11px] text-muted-foreground">
        MO Presupuestada = teórico/m² × m² construidos. Sobrecosto = gastada − presupuestada (rojo =
        más caro que lo presupuestado). Eficiencia = teórico ÷ real × 100 (≥100% = más eficiente).
      </p>

      {/* Gráfico gastada vs presupuestada */}
      <BarrasGastoVsPresup gastada={calc.moTotalGastada} presupuestada={calc.moPresupuestada} />

      {/* Resumen del mes — MO por semana */}
      <ResumenMesMOVista semanas={semanas} semanaSel={semana} filtroVenta={filtroVenta} />

      {/* Distribución por obra */}
      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-zinc-50 px-3 py-1.5">
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Distribución por obra
          </span>
          <div className="flex items-center gap-2">
            <TogglePeriodo periodo={periodo} onCambiar={setPeriodo} />
            <Input
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar obra…"
              className="h-7 w-44 text-xs"
            />
          </div>
        </div>
        {periodo === 'acumulado' && (
          <div className="flex items-center gap-2 border-b border-border bg-zinc-50/50 px-3 py-1.5 text-[11px] text-muted-foreground">
            {acumLoading ? (
              <>
                <CircleNotch className="h-3 w-3 animate-spin" weight="bold" aria-hidden="true" />
                Calculando acumulado…
              </>
            ) : acumD?.data && acumD.data.n_semanas > 0 ? (
              <>
                Acumulado de <strong>{acumD.data.n_semanas}</strong> semana(s) con datos
                {acumD.data.primera_semana &&
                  ` (${fecha(acumD.data.primera_semana)} — ${fecha(acumD.data.ultima_semana ?? acumD.data.primera_semana)})`}
                . m² a la fecha de la semana seleccionada.
              </>
            ) : (
              'Sin nómina/horas registradas todavía.'
            )}
          </div>
        )}
        <Table>
          <TableHeader>
            <TableRow>
              <ThOrden col="codigo" orden={orden} onOrdenar={setOrden} align="left">
                Obra
              </ThOrden>
              <ThOrden col="hrs" orden={orden} onOrdenar={setOrden}>
                Horas
              </ThOrden>
              <ThOrden col="pctHoras" orden={orden} onOrdenar={setOrden}>
                % horas
              </ThOrden>
              <ThOrden col="nominaAsig" orden={orden} onOrdenar={setOrden}>
                Nómina asignada
              </ThOrden>
              <ThOrden col="sc" orden={orden} onOrdenar={setOrden}>
                Subcontratos
              </ThOrden>
              <ThOrden col="moTotal" orden={orden} onOrdenar={setOrden}>
                M.O. total
              </ThOrden>
              <ThOrden col="m2" orden={orden} onOrdenar={setOrden}>
                {periodo === 'acumulado' ? 'm² acum.' : 'm² sem.'}
              </ThOrden>
              <ThOrden col="costoM2" orden={orden} onOrdenar={setOrden}>
                Costo M.O./m²
              </ThOrden>
            </TableRow>
          </TableHeader>
          <TableBody>
            {vista.map((o) => (
              <TableRow key={o.codigo}>
                <TableCell className="font-mono text-xs font-semibold">{o.codigo}</TableCell>
                <TableCell className="text-right tabular-nums">{fmt(o.hrs)}</TableCell>
                <TableCell className="text-right tabular-nums text-muted-foreground">
                  {o.pctHoras > 0 ? `${o.pctHoras.toFixed(1)}%` : '—'}
                </TableCell>
                <TableCell className="text-right font-mono tabular-nums">
                  {o.nominaAsig > 0 ? formatearCRC(o.nominaAsig) : '—'}
                </TableCell>
                <TableCell className="text-right font-mono tabular-nums">
                  {o.sc > 0 ? formatearCRC(o.sc) : '—'}
                </TableCell>
                <TableCell className="text-right font-mono tabular-nums font-semibold">
                  {formatearCRC(o.moTotal)}
                </TableCell>
                <TableCell className="text-right tabular-nums text-muted-foreground">
                  {fmt(o.m2)}
                </TableCell>
                <TableCell className="text-right font-mono tabular-nums">
                  {o.costoM2 > 0 ? formatearCRC(o.costoM2) : '—'}
                </TableCell>
              </TableRow>
            ))}
            {vista.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="py-6 text-center text-sm text-muted-foreground">
                  {filtro
                    ? `Ninguna obra coincide con «${busqueda}».`
                    : periodo === 'acumulado'
                      ? 'Sin horas ni subcontratos registrados todavía.'
                      : 'Sin horas ni subcontratos para esta semana.'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

function fmt(n: number): string {
  return n.toLocaleString('en-US', { maximumFractionDigits: 1 });
}

function fmt2(n: number): string {
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fecha(iso: string): string {
  return iso.slice(0, 10).split('-').reverse().join('/');
}

/** Switch Semana / Acumulado para la tabla de distribución por obra. */
function TogglePeriodo({
  periodo,
  onCambiar,
}: {
  periodo: Periodo;
  onCambiar: (p: Periodo) => void;
}) {
  const opciones: { valor: Periodo; label: string }[] = [
    { valor: 'semana', label: 'Semana' },
    { valor: 'acumulado', label: 'Acumulado' },
  ];
  return (
    <div className="flex overflow-hidden rounded-md border border-border text-xs">
      {opciones.map((o) => (
        <button
          key={o.valor}
          type="button"
          onClick={() => onCambiar(o.valor)}
          className={cn(
            'px-2 py-1 font-medium transition-colors',
            periodo === o.valor
              ? 'bg-adelante-primary text-adelante-foreground'
              : 'bg-white text-muted-foreground hover:bg-zinc-50',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Gráfico simple (sin dependencias): dos barras MO gastada vs presupuestada. */
function BarrasGastoVsPresup({
  gastada,
  presupuestada,
}: {
  gastada: number;
  presupuestada: number;
}) {
  const max = Math.max(gastada, presupuestada, 1);
  const Barra = ({ label, val, color }: { label: string; val: number; color: string }) => (
    <div className="flex items-center gap-2">
      <span className="w-36 shrink-0 text-xs text-muted-foreground">{label}</span>
      <div className="h-5 flex-1 overflow-hidden rounded bg-zinc-100">
        <div className={cn('h-5 rounded', color)} style={{ width: `${(val / max) * 100}%` }} />
      </div>
      <span className="w-28 shrink-0 text-right font-mono text-xs tabular-nums">
        {formatearCRC(val)}
      </span>
    </div>
  );
  return (
    <Card className="space-y-2 p-3">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        MO Gastada vs Presupuestada
      </p>
      <Barra label="MO Gastada" val={gastada} color="bg-blue-500" />
      <Barra label="MO Presupuestada" val={presupuestada} color="bg-adelante-primary" />
    </Card>
  );
}

/** Encabezado ordenable de la distribución por obra. */
function ThOrden({
  col,
  orden,
  onOrdenar,
  children,
  align = 'right',
}: {
  col: MoCol;
  orden: MoOrden;
  onOrdenar: (updater: (prev: MoOrden) => MoOrden) => void;
  children: ReactNode;
  align?: 'left' | 'right';
}) {
  const activo = orden.col === col;
  return (
    <TableHead className={align === 'right' ? 'text-right' : undefined}>
      <button
        type="button"
        onClick={() =>
          onOrdenar((prev) =>
            prev.col === col
              ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
              : { col, dir: col === 'codigo' ? 'asc' : 'desc' },
          )
        }
        className={cn(
          'inline-flex w-full items-center gap-1 hover:text-foreground',
          align === 'right' ? 'justify-end' : 'justify-start',
        )}
      >
        {children}
        {activo ? (
          orden.dir === 'asc' ? (
            <ArrowUp className="h-3 w-3" weight="bold" aria-hidden="true" />
          ) : (
            <ArrowDown className="h-3 w-3" weight="bold" aria-hidden="true" />
          )
        ) : (
          <ArrowsDownUp className="h-3 w-3 opacity-30" weight="bold" aria-hidden="true" />
        )}
      </button>
    </TableHead>
  );
}

function Seccion({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <div className="space-y-2">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {titulo}
      </p>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">{children}</div>
    </div>
  );
}

function Kpi({
  label,
  value,
  accent,
  small,
}: {
  label: string;
  value: string;
  accent?: 'lime' | 'red';
  small?: boolean;
}) {
  return (
    <Card className="p-3">
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p
        className={cn(
          'font-semibold tabular-nums',
          small ? 'text-lg' : 'text-2xl',
          accent === 'lime' && 'text-adelante-primary-dark',
          accent === 'red' && 'text-red-600',
        )}
      >
        {value}
      </p>
    </Card>
  );
}
